import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/DashboardLayout';
import { clientsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  PaintBrush, Lightning, CircleNotch, Image, Palette, TestTube, Trophy, Plus
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('nx_token');
const authHeader = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

export default function CreativeLabPage() {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [prompt, setPrompt] = useState('');
  const [format, setFormat] = useState('social_post');
  const [industry, setIndustry] = useState('');
  const [generating, setGenerating] = useState(false);
  const [creatives, setCreatives] = useState([]);
  const [abTests, setAbTests] = useState([]);
  const [brandKit, setBrandKit] = useState({ brand_name: '', primary_color: '#1A56DB', secondary_color: '#00D4AA', tone: 'professional', style_notes: '' });
  const [savingKit, setSavingKit] = useState(false);
  const [genVariations, setGenVariations] = useState(false);
  const [variations, setVariations] = useState(null);

  useEffect(() => {
    clientsAPI.list().then(r => {
      setClients(r.data);
      if (r.data.length > 0) {
        setSelectedClient(r.data[0].id);
        setIndustry(r.data[0].industry || '');
      }
    });
  }, []);

  useEffect(() => {
    if (!selectedClient) return;
    const c = clients.find(c => c.id === selectedClient);
    if (c) setIndustry(c.industry || '');
    axios.get(`${API_BASE}/creative/list/${selectedClient}`, authHeader()).then(r => setCreatives(r.data)).catch(() => {});
    axios.get(`${API_BASE}/creative/ab-tests/${selectedClient}`, authHeader()).then(r => setAbTests(r.data)).catch(() => {});
    axios.get(`${API_BASE}/creative/brand-kit/${selectedClient}`, authHeader()).then(r => setBrandKit(r.data)).catch(() => {});
  }, [selectedClient, clients]);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API_BASE}/creative/generate`, { client_id: selectedClient, prompt, format, industry, language: 'en' }, authHeader());
      setCreatives(prev => [res.data, ...prev]);
      toast.success('Creative generated!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const handleVariations = async () => {
    if (!prompt.trim()) return;
    setGenVariations(true);
    try {
      const res = await axios.post(`${API_BASE}/creative/variations?count=3`, { client_id: selectedClient, prompt, format, industry, language: 'en' }, authHeader());
      setVariations(res.data);
      toast.success(`${res.data.variations?.length || 0} variations generated`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setGenVariations(false); }
  };

  const handleCreateABTest = async () => {
    const imageCreatives = creatives.filter(c => c.images?.length > 0).slice(0, 4);
    if (imageCreatives.length < 2) { toast.error('Need at least 2 creatives with images for A/B testing'); return; }
    try {
      const res = await axios.post(`${API_BASE}/creative/ab-test`, {
        client_id: selectedClient, name: `A/B Test ${new Date().toLocaleDateString()}`,
        creative_ids: imageCreatives.map(c => c.id), platform: 'Meta', budget: 200, duration_days: 7
      }, authHeader());
      setAbTests(prev => [res.data, ...prev]);
      toast.success('A/B test created');
    } catch (err) { toast.error('Failed to create test'); }
  };

  const handleSaveBrandKit = async () => {
    setSavingKit(true);
    try {
      await axios.post(`${API_BASE}/creative/brand-kit`, { ...brandKit, client_id: selectedClient, industry }, authHeader());
      toast.success('Brand kit saved');
    } catch { toast.error('Failed to save'); }
    finally { setSavingKit(false); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="creative-lab-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight">
              Creative Lab
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">AI-powered ad creative generation & multivariate testing</p>
          </div>
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger data-testid="creative-client-select" className="w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]">
              <SelectValue placeholder="Select client" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
              {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{c.display_name || c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="generate" className="space-y-4">
          <TabsList className="bg-[var(--surface)] border border-[var(--border)]">
            <TabsTrigger value="generate" data-testid="tab-generate" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">
              <PaintBrush size={14} className="mr-1.5" /> Generate
            </TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">
              <Image size={14} className="mr-1.5" /> Gallery ({creatives.length})
            </TabsTrigger>
            <TabsTrigger value="ab-tests" data-testid="tab-ab-tests" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">
              <TestTube size={14} className="mr-1.5" /> A/B Tests ({abTests.length})
            </TabsTrigger>
            <TabsTrigger value="brand-kit" data-testid="tab-brand-kit" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">
              <Palette size={14} className="mr-1.5" /> Brand Kit
            </TabsTrigger>
          </TabsList>

          {/* Generate Tab */}
          <TabsContent value="generate" className="space-y-4">
            <div className="nx-metric-card rounded-lg" data-testid="creative-generator">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                <Lightning size={18} className="inline mr-2 text-[var(--accent)]" weight="fill" />
                Creative Generator — Gemini Nano Banana
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Creative Brief</label>
                  <textarea data-testid="creative-prompt-input" value={prompt}
                    onChange={e => setPrompt(e.target.value)} rows={3}
                    placeholder="Describe the ad creative you need... e.g., 'Luxury watch campaign targeting Gulf professionals, gold and black color scheme'"
                    className="w-full bg-[var(--surface-sunken)] border border-[var(--border)] text-[var(--text-primary)] rounded-md px-3 py-2 text-sm placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)] focus:outline-none resize-none" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Format</label>
                    <Select value={format} onValueChange={setFormat}>
                      <SelectTrigger data-testid="creative-format-select" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                        <SelectItem value="social_post" className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">Social Post (1080x1080)</SelectItem>
                        <SelectItem value="banner" className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">Banner (1200x628)</SelectItem>
                        <SelectItem value="story" className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">Story (1080x1920)</SelectItem>
                        <SelectItem value="display_ad" className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">Display Ad (300x250)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Industry</label>
                    <Input data-testid="creative-industry-input" value={industry} onChange={e => setIndustry(e.target.value)}
                      className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" />
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button data-testid="generate-creative-btn" onClick={handleGenerate} disabled={generating || !prompt.trim()}
                    className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white disabled:opacity-50 flex-1">
                    {generating ? <><CircleNotch size={16} className="animate-spin mr-2" /> Generating...</> : <><PaintBrush size={16} className="mr-2" /> Generate Creative</>}
                  </Button>
                  <Button data-testid="generate-variations-btn" onClick={handleVariations} disabled={genVariations || !prompt.trim()}
                    variant="outline" className="border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--accent)]">
                    {genVariations ? <CircleNotch size={16} className="animate-spin" /> : <><Plus size={16} className="mr-1" /> 3 Variations</>}
                  </Button>
                </div>
              </div>
            </div>

            {/* Variations Result */}
            {variations && (
              <div className="nx-metric-card rounded-lg animate-fade-in-up" data-testid="variations-result">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">Generated Variations</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {variations.variations?.map((v, i) => (
                    <div key={v.id} className="rounded border border-[var(--border)] overflow-hidden">
                      {v.images?.[0] ? (
                        <img src={`${API_BASE}/creative/image/${v.images[0].id}`} alt={`Variation ${i + 1}`}
                          className="w-full h-48 object-cover" />
                      ) : (
                        <div className="w-full h-48 bg-[var(--surface-sunken)] flex items-center justify-center text-[var(--text-tertiary)]">
                          {v.error ? 'Generation failed' : 'No image'}
                        </div>
                      )}
                      <div className="p-3">
                        <span className="nx-tag nx-tag-blue text-[10px]">{v.style}</span>
                        {v.ai_notes && <p className="text-xs text-[var(--text-secondary)] mt-2 line-clamp-2">{v.ai_notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery" className="space-y-4">
            {creatives.length === 0 ? (
              <div className="nx-metric-card rounded-lg text-center py-12">
                <Image size={40} className="mx-auto mb-3 text-[var(--text-tertiary)]" />
                <p className="text-[var(--text-secondary)]">No creatives yet. Generate your first one!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {creatives.map((c, i) => (
                  <div key={c.id} className="nx-metric-card rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: `${i * 40}ms` }}
                    data-testid={`creative-${c.id}`}>
                    {c.images?.[0] ? (
                      <img src={`${API_BASE}/creative/image/${c.images[0].id}`} alt={c.prompt}
                        className="w-full h-48 object-cover" />
                    ) : (
                      <div className="w-full h-48 bg-[var(--surface-sunken)] flex items-center justify-center text-[var(--text-tertiary)] text-sm">Processing...</div>
                    )}
                    <div className="p-3">
                      <p className="text-xs text-[var(--text-primary)] line-clamp-2 mb-2">{c.prompt}</p>
                      <div className="flex items-center gap-2">
                        <span className="nx-tag nx-tag-blue">{c.format}</span>
                        <span className="nx-tag nx-tag-gray">{c.industry}</span>
                        <span className="text-[10px] text-[var(--text-tertiary)] font-mono ml-auto">{new Date(c.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* A/B Tests Tab */}
          <TabsContent value="ab-tests" className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)]">Multivariate Tests</h3>
              <Button data-testid="create-ab-test-btn" onClick={handleCreateABTest} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
                <TestTube size={14} className="mr-2" /> New A/B Test
              </Button>
            </div>
            {abTests.length === 0 ? (
              <div className="nx-metric-card rounded-lg text-center py-12">
                <TestTube size={40} className="mx-auto mb-3 text-[var(--text-tertiary)]" />
                <p className="text-[var(--text-secondary)]">No A/B tests yet. Generate creatives first, then test them.</p>
              </div>
            ) : (
              abTests.map(test => (
                <div key={test.id} className="nx-metric-card rounded-lg" data-testid={`ab-test-${test.id}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="font-display text-sm text-[var(--text-primary)] font-bold">{test.name}</h4>
                      <p className="text-xs text-[var(--text-tertiary)] font-mono">{test.platform} &middot; ${test.budget} &middot; {test.duration_days}d</p>
                    </div>
                    <span className={`nx-tag ${test.status === 'running' ? 'nx-tag-green' : 'nx-tag-gray'}`}>{test.status}</span>
                  </div>
                  <table className="nx-table">
                    <thead>
                      <tr><th>Creative</th><th>Impressions</th><th>Clicks</th><th>CTR</th><th>Conversions</th><th>Confidence</th><th>Winner</th></tr>
                    </thead>
                    <tbody>
                      {Object.entries(test.results || {}).map(([cid, r]) => (
                        <tr key={cid}>
                          <td className="font-mono text-xs text-[var(--accent)]">{cid.substring(0, 8)}...</td>
                          <td className="font-mono">{r.impressions?.toLocaleString()}</td>
                          <td className="font-mono">{r.clicks}</td>
                          <td className="font-mono font-bold text-[var(--text-primary)]">{r.ctr}%</td>
                          <td className="font-mono">{r.conversions}</td>
                          <td className="font-mono">{r.confidence}%</td>
                          <td>{test.winner === cid ? <Trophy size={16} className="text-[var(--warning)]" weight="fill" /> : null}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-[10px] text-[var(--text-tertiary)] font-mono mt-2 italic">
                    {test.source === 'mock' ? 'Simulated results — connect Meta/Google APIs for real performance data' : 'Live performance data'}
                  </p>
                </div>
              ))
            )}
          </TabsContent>

          {/* Brand Kit Tab */}
          <TabsContent value="brand-kit" className="space-y-4">
            <div className="nx-metric-card rounded-lg" data-testid="brand-kit-editor">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                <Palette size={18} className="inline mr-2 text-[var(--accent)]" weight="duotone" /> Brand Kit
              </h3>
              <p className="text-xs text-[var(--text-secondary)] mb-4">Define your client's brand identity to ensure all AI-generated creatives align with brand guidelines.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Brand Name</label>
                  <Input data-testid="brand-name-input" value={brandKit.brand_name || ''}
                    onChange={e => setBrandKit({...brandKit, brand_name: e.target.value})}
                    placeholder="Brand name" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" />
                </div>
                <div>
                  <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Tone</label>
                  <Select value={brandKit.tone || 'professional'} onValueChange={v => setBrandKit({...brandKit, tone: v})}>
                    <SelectTrigger data-testid="brand-tone-select" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                      {['professional', 'playful', 'luxury', 'bold', 'minimal', 'traditional'].map(t => (
                        <SelectItem key={t} value={t} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)] capitalize">{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Primary Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={brandKit.primary_color || '#1A56DB'} onChange={e => setBrandKit({...brandKit, primary_color: e.target.value})}
                      className="w-10 h-10 rounded border border-[var(--border)] bg-transparent cursor-pointer" />
                    <Input value={brandKit.primary_color || ''} onChange={e => setBrandKit({...brandKit, primary_color: e.target.value})}
                      className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] font-mono" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Secondary Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={brandKit.secondary_color || '#00D4AA'} onChange={e => setBrandKit({...brandKit, secondary_color: e.target.value})}
                      className="w-10 h-10 rounded border border-[var(--border)] bg-transparent cursor-pointer" />
                    <Input value={brandKit.secondary_color || ''} onChange={e => setBrandKit({...brandKit, secondary_color: e.target.value})}
                      className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] font-mono" />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Style Notes</label>
                  <textarea data-testid="brand-style-notes" value={brandKit.style_notes || ''}
                    onChange={e => setBrandKit({...brandKit, style_notes: e.target.value})} rows={3}
                    placeholder="Additional style guidelines... e.g., 'Use geometric patterns, avoid stock photos, prefer dark backgrounds'"
                    className="w-full bg-[var(--surface-sunken)] border border-[var(--border)] text-[var(--text-primary)] rounded-md px-3 py-2 text-sm placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)] focus:outline-none resize-none" />
                </div>
              </div>
              <Button data-testid="save-brand-kit-btn" onClick={handleSaveBrandKit} disabled={savingKit}
                className="mt-4 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
                {savingKit ? 'Saving...' : 'Save Brand Kit'}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
